import { computed, ref } from '@vue/composition-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { HSCode, IHSCodeData } from '@/models';
import { MaybeRef } from '@tanstack/vue-query/build/lib/types';
import providerInstance from '@/services/provider';

export function useFetchHSCodes() {
  const queryKey = ref(['hsCodes']);

  const { isLoading, isError, error, data: hsCodes } = useQuery({
    queryKey: queryKey,
    queryFn: () => providerInstance.harmonizedSystem.fetchHSCodes(),
    enabled: true,
    select: data => {
      return data.map(hsCode => new HSCode(hsCode));
    }
  });

  return { queryKey, hsCodes, error, isError, isLoading };
}

export function useFetchHSCode(code: MaybeRef<string>) {
  const hsCodeRef = ref(code);

  const queryKey = ref(['hsCodes', hsCodeRef]);

  const { data: hsCode, isLoading, error } = useQuery({
    queryKey: queryKey,
    enabled: computed(() => Boolean(hsCodeRef.value)),
    queryFn: () =>
      providerInstance.harmonizedSystem.fetchHSCode(hsCodeRef.value),
    select: data => new HSCode(data)
  });

  return { hsCode, isLoading, error, queryKey };
}

export function useCreateHSCode() {
  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  } = useMutation({
    mutationKey: ['create-hsCode'],
    mutationFn: (hsCode: IHSCodeData) =>
      providerInstance.harmonizedSystem.createHSCode(hsCode),

    onSuccess: data => {
      queryClient.setQueryData(['hsCodes', data.code], data);
      queryClient.invalidateQueries({ queryKey: ref(['hsCodes']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  };
}

export function useUpdateHSCode() {
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['update-hsCode'],
    mutationFn: (hsCode: IHSCodeData) =>
      providerInstance.harmonizedSystem.updateHSCode(hsCode),

    onSuccess: data => {
      queryClient.setQueryData(['hsCodes', data.code], data);
      queryClient.invalidateQueries({
        queryKey: ref(['hsCodes', data.code])
      });
      queryClient.invalidateQueries({ queryKey: ref(['hsCodes']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useDeleteHSCode() {
  const mutationKey = ref(['delete-hsCode']);

  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: mutationKey.value,
    mutationFn: (code: string) =>
      providerInstance.harmonizedSystem.deleteHSCode(code),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ref(['hsCodes']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}

export function useUploadHSCode() {
  const queryClient = useQueryClient();

  const { isLoading, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ['updload-hsCode'],
    mutationFn: (file: File) =>
      providerInstance.harmonizedSystem.uploadHSCodes(file),

    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ref(['hsCodes']) });
    }
  });
  return {
    isLoading,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  };
}
